import { useState } from 'react'
import {Trash2, Download, ArrowUpDown, Mail, Phone, RefreshCw, Inbox, Loader2} from 'lucide-react'
import toast from 'react-hot-toast'
import { formatUSD, MODE_LABELS } from '@/lib/freight'
import type { FreightLead, SortDir, SortKey } from '@/hooks/useFreightLeads'

interface LeadsTableProps {
  leads: FreightLead[]
  loading: boolean
  onDelete: (id: string) => Promise<void>
  onRefresh: () => void
  onExportAll: () => Promise<FreightLead[]>
  sortKey: SortKey
  sortDir: SortDir
  onToggleSort: (key: SortKey) => void
}

const MODE_BADGE: Record<string, string> = {
  LTL: 'bg-blue-50 text-blue-700',
  FTL: 'bg-amber-50 text-amber-700',
  OCEAN: 'bg-cyan-50 text-cyan-700',
  AIR: 'bg-violet-50 text-violet-700',
}

function formatDate(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function toCsv(rows: FreightLead[]): string {
  const headers = [
    'Full Name', 'Business Email', 'Phone Number', 'Transport Mode',
    'Estimated Rate', 'Origin', 'Destination', 'Cargo Ready Date', 'Created At',
  ]
  const lines = rows.map((r) => [
    r.fullName, r.businessEmail, r.phoneNumber, MODE_LABELS[r.transportMode] || r.transportMode,
    r.estimatedRate, r.origin || '', r.destination || '', r.cargoReadyDate, r.createdAt,
  ].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
  return [headers.join(','), ...lines].join('\n')
}

function LeadsTable({ leads, loading, onDelete, onRefresh, onExportAll, sortKey, sortDir, onToggleSort }: LeadsTableProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  const handleDelete = async (id: string) => {
    setDeletingId(id)
    try {
      await onDelete(id)
    } finally {
      setDeletingId(null)
      setConfirmId(null)
    }
  }

  const exportCsv = async () => {
    setExporting(true)
    try {
      const rows = await onExportAll()
      if (rows.length === 0) {
        toast.error('No leads to export for the current filters.')
        return
      }
      const csv = toCsv(rows)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `freight-leads-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
      toast.success(`Exported ${rows.length} lead${rows.length === 1 ? '' : 's'}`)
    } finally {
      setExporting(false)
    }
  }

  const SortHeader = ({ label, k }: { label: string; k: SortKey }) => (
    <button
      onClick={() => onToggleSort(k)}
      className={`inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors ${
        sortKey === k ? 'text-[#10B981]' : 'text-slate-500 hover:text-[#0F172A]'
      }`}
    >
      {label}
      <ArrowUpDown className="w-3 h-3" />
    </button>
  )

  return (
    <div>
      <div className="flex items-center justify-end gap-2 p-4 border-b border-slate-200">
        <button
          onClick={onRefresh}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <button
          onClick={exportCsv}
          disabled={exporting || leads.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#10B981] hover:bg-[#0ea472] disabled:opacity-50 text-white text-sm font-semibold transition-colors shadow-sm"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Export CSV
        </button>
      </div>

      {loading ? (
        <div className="p-16 text-center text-slate-500">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#10B981]" />
          <p className="mt-3 text-sm">Loading leads...</p>
        </div>
      ) : leads.length === 0 ? (
        <div className="p-16 text-center text-slate-500">
          <Inbox className="w-10 h-10 mx-auto text-slate-300" />
          <p className="mt-3 text-sm font-medium">No leads match your filters</p>
          <p className="text-xs text-slate-400">Try adjusting or resetting your filters.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3"><SortHeader label="Contact" k="fullName" /></th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Lane</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Mode</th>
                <th className="text-left px-4 py-3"><SortHeader label="Estimate" k="estimatedRate" /></th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Cargo Ready</th>
                <th className="text-left px-4 py-3"><SortHeader label="Received" k="createdAt" /></th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leads.map((lead) => (
                <tr key={lead._id} className="hover:bg-slate-50/70 transition-colors">
                  <td className="px-4 py-3.5">
                    <p className="font-semibold text-[#0F172A]">{lead.fullName}</p>
                    <div className="mt-1 flex flex-col gap-0.5 text-xs text-slate-500">
                      <a href={`mailto:${lead.businessEmail}`} className="inline-flex items-center gap-1 hover:text-[#10B981]">
                        <Mail className="w-3 h-3" /> {lead.businessEmail}
                      </a>
                      <a href={`tel:${lead.phoneNumber}`} className="inline-flex items-center gap-1 hover:text-[#10B981]">
                        <Phone className="w-3 h-3" /> {lead.phoneNumber}
                      </a>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-slate-600">
                    {lead.origin || '—'} <span className="text-slate-300">→</span> {lead.destination || '—'}
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${MODE_BADGE[lead.transportMode] || 'bg-slate-100 text-slate-600'}`}>
                      {MODE_LABELS[lead.transportMode] || lead.transportMode}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 font-semibold text-[#0F172A]">{formatUSD(lead.estimatedRate || 0)}</td>
                  <td className="px-4 py-3.5 text-slate-600">{formatDate(lead.cargoReadyDate)}</td>
                  <td className="px-4 py-3.5 text-slate-500">{formatDate(lead.createdAt)}</td>
                  <td className="px-4 py-3.5 text-right">
                    {confirmId === lead._id ? (
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => handleDelete(lead._id)}
                          disabled={deletingId === lead._id}
                          className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"
                        >
                          {deletingId === lead._id ? 'Deleting...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setConfirmId(null)}
                          className="text-xs font-medium text-slate-400 hover:text-slate-600"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmId(lead._id)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        aria-label="Delete lead"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default LeadsTable

